<script setup lang="ts">
import { onBeforeUnmount, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { Menu, PanelLeftClose, PanelLeftOpen } from 'lucide-vue-next';
import DButton from '../../design/components/DButton.vue';
import ApiStatus from './ApiStatus.vue';
import BrandBar from './BrandBar.vue';
import NavDrawer from './NavDrawer.vue';
import NavLinks from './NavLinks.vue';
import UserMenu from './UserMenu.vue';
import NoticeHost from './NoticeHost.vue';
import PlanBanners from '../plans/PlanBanners.vue';
import OwnershipOffers from '../orgs/OwnershipOffers.vue';
import { navTarget, PRIMARY_NAV, unreadLabel } from './navigation.ts';
import { useInboxStore } from '../../app/stores/inbox.ts';
import { useWorkspaceStore } from '../../app/stores/workspace.ts';
import { useRealtime } from '../../app/realtime/RealtimeClient.ts';
import { useSidebar } from '../../app/composables/useSidebar.ts';
import { useShortcut } from '../../app/composables/useShortcut.ts';
import { usePageTitle } from '../../app/composables/usePageTitle.ts';
import { useBreakpoint } from '../../app/composables/useBreakpoint.ts';

const route = useRoute();
const { collapsed, toggleCollapsed, drawerOpen } = useSidebar();
useShortcut('[', toggleCollapsed);
// Any navigation (including from dialogs opened inside the drawer) closes the drawer.
watch(
  () => route.fullPath,
  () => (drawerOpen.value = false),
);

// Widening past the desktop breakpoint swaps the drawer for the sidebar.
const { isDesktop } = useBreakpoint();
watch(isDesktop, (desktop) => {
  if (desktop) drawerOpen.value = false;
});

const { title } = usePageTitle();

// Unread badges: counted now, and again whenever the server says they changed.
const workspace = useWorkspaceStore();
const inbox = useInboxStore();
const realtime = useRealtime();
let stopListening: (() => void) | null = null;
onMounted(() => {
  void inbox.refresh();
  stopListening = realtime.onNotifications(() => void inbox.refresh());
});
onBeforeUnmount(() => stopListening?.());
</script>

<template>
  <div class="app-shell" :data-sidebar="collapsed ? 'rail' : 'full'">
    <!-- Desktop -->
    <aside class="app-shell__sidebar sidebar" aria-label="Sidebar">
      <NavLinks />
      <div class="sidebar__footer">
        <UserMenu side="right" />
        <span class="spacer sidebar__label"><ApiStatus /></span>
        <DButton
          class="sidebar__collapse"
          variant="ghost"
          size="sm"
          icon
          :aria-label="collapsed ? 'Expand sidebar ([)' : 'Collapse sidebar ([)'"
          :title="collapsed ? 'Expand sidebar ([)' : 'Collapse sidebar ([)'"
          @click="toggleCollapsed"
        >
          <PanelLeftOpen v-if="collapsed" :size="15" :stroke-width="1.75" />
          <PanelLeftClose v-else :size="15" :stroke-width="1.75" />
        </DButton>
      </div>
    </aside>

    <!-- Phone and tablet: top bar with the navigation drawer -->
    <header class="topbar">
      <DButton variant="ghost" icon aria-label="Open navigation" @click="drawerOpen = true">
        <Menu :size="18" :stroke-width="1.75" />
      </DButton>
      <BrandBar compact />
      <p class="topbar__title truncate">{{ title }}</p>
      <UserMenu />
    </header>

    <main class="app-shell__main">
      <div class="account-banners">
        <PlanBanners />
        <OwnershipOffers />
      </div>
      <slot />
    </main>

    <!-- Phone only -->
    <nav class="tabbar" aria-label="Primary">
      <RouterLink
        v-for="item in PRIMARY_NAV"
        :key="item.to"
        :to="navTarget(item, workspace.currentSlug)"
        class="tabbar__item"
        :class="{ 'tabbar__item--disabled': !item.enabled }"
        active-class=""
        exact-active-class="tabbar__item--active"
        :aria-disabled="!item.enabled || undefined"
        :tabindex="item.enabled ? undefined : -1"
      >
        <span class="nav-icon">
          <component :is="item.icon" :size="20" :stroke-width="1.75" aria-hidden="true" />
          <span
            v-if="item.counts && inbox.countFor(workspace.currentSlug)"
            class="count-badge count-badge--corner"
            :aria-label="unreadLabel(inbox.countFor(workspace.currentSlug))"
            >{{ Math.min(inbox.countFor(workspace.currentSlug), 99) }}</span
          >
        </span>
        {{ item.label }}
      </RouterLink>
    </nav>

    <NavDrawer v-model:open="drawerOpen" />
    <NoticeHost />
  </div>
</template>
